"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { Images, Icons } from "@assets/assets";
import { savePortfolioAndWhitPaper } from "@/lib/ApiClient";
import {
  portfolioModalInterface,
  whitePaperAndPortfolioInterface,
} from "@/lib/Types";
import clsx from "clsx";
import dynamic from "next/dynamic";
import Script from "next/script";
import { useRouter } from "next/navigation";
import Loader from "@/components/Loader";

const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});
interface SuccessStoryPopupProps {
  onClose?: () => void;
  portfolioDetails: portfolioModalInterface | undefined;
  loading?: boolean;
}

const initialState: whitePaperAndPortfolioInterface = {
  name: "",
  email: "",
  companyName: "",
  msTeamWhatsapp: "",
  requirement: "",
  agreeToReceive: false,
  captcha: "",
  pdfUrl: "",
  pdf_file: "",
  type: "",
};

export default function SuccessStoryPopup({
  onClose,
  portfolioDetails,
  loading = false,
}: SuccessStoryPopupProps) {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] =
    useState<whitePaperAndPortfolioInterface>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [submissionMessage, setSubmissionMessage] = useState<string>("");

  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    setMounted(true);
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [formData.requirement]);

  const handleChange =
    (key: keyof whitePaperAndPortfolioInterface) =>
      (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >,
      ) => {
        const value =
          e.target.type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : e.target.value;
        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
      };

  const [recaptchaToken, setRecaptchaToken] = useState<string>("");
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const ensureLoadRecaptcha = useCallback(() => setLoadRecaptcha(true), []);

  const validateForm = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData?.name || formData?.name.trim().length < 2) {
      newErrors.fullName = "Please enter your full name.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!formData.companyName || formData.companyName.trim().length === 0) {
      newErrors.companyName = "Please enter your company name.";
    }
    let captchaToken = "";
    if (recaptchaSiteKey) {
      captchaToken = recaptchaToken || "";
      if (!captchaToken) {
        newErrors.captcha = "Please complete the captcha.";
      }
    }
    return { newErrors, token: captchaToken };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisable(true);
    setSubmissionStatus("idle");
    setSubmissionMessage("");

    try {
      const { newErrors, token } = await validateForm();
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setButtonDisable(false);
        setSubmissionStatus("error");
        setSubmissionMessage(
          "Please fix the highlighted errors and try again.",
        );
        return;
      }

      const payload = {
        ...formData,
        captcha: token,
        pdfUrl: portfolioDetails?.pdfFile || "1767014833_how-to-choose-the-right-app-development-firm.pdf",
        type: "portfolio",
      } as whitePaperAndPortfolioInterface;

      setSubmissionStatus("submitting");
      setSubmissionMessage("Submitting...");

      const data = await savePortfolioAndWhitPaper(payload);

      if (data?.success) {
        router.push("/thank-you?type=success-stories");
        setSubmissionStatus("success");
        setSubmissionMessage(
          "Thank you — the PDF has been sent. You will receive it shortly.",
        );
        setFormData(initialState);
        try {
          (window as any).grecaptcha?.reset?.();
        } catch (error: any) {
          console.log(error?.message);
        }
      } else {
        setSubmissionStatus("error");
        setSubmissionMessage(
          data?.message ||
          "Something went wrong while submitting. Please try again later.",
        );
      }
    } catch (err) {
      console.error(err);
      setSubmissionStatus("error");
      setSubmissionMessage(
        "An unexpected error occurred. Please try again later.",
      );
    } finally {
      setButtonDisable(false);
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div
      className={clsx(
        "fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-500",
        isVisible ? "opacity-100" : "opacity-0",
      )}
    >
      <div
        className={clsx(
          "relative w-screen h-screen bg-white rounded-none overflow-hidden shadow-2xl transition-transform duration-500",
          isVisible ? "translate-y-0" : "translate-y-10",
        )}
      >
        <button
          type="button"
          aria-label="Close"
          className="absolute right-8 top-4 z-50 w-12 h-12 rounded-full bg-white hover:bg-gray-100 grid place-items-center border border-[#ECF0F6] transition-all"
          onClick={onClose}
        >
          <span className="relative block w-4 h-4">
            <span className="absolute left-0 top-1/2 h-[2px] w-full bg-[#0A0D14] rotate-45 -translate-y-1/2"></span>
            <span className="absolute left-0 top-1/2 h-[2px] w-full bg-[#0A0D14] -rotate-45 -translate-y-1/2"></span>
          </span>
        </button>

        {loading ? (
          <div className="w-full h-full flex justify-center items-center">
            <Loader size={80} />
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden">
            {/* Left Content */}
            <div className="w-full lg:w-[45%] bg-[#0A1628] px-6 lg:px-12 py-8 lg:py-6 flex flex-col justify-start shrink-0">
              <div className="mb-8">
                <Image
                  src={Images?.logoIcon}
                  alt="Logo"
                  width={60}
                  height={20}
                  className="brightness-0 invert"
                />
              </div>
              <div className="relative w-full hidden lg:block aspect-[16/9] lg:aspect-[4/3] overflow-hidden mb-6 lg:mb-8 rounded-lg">
                {portfolioDetails?.newHomepageBg &&
                  < Image
                    src={`${process.env.NEXT_PUBLIC_AWS_BASE_URL}/${portfolioDetails?.newHomepageBg}`}
                    alt="Project"
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />}
              </div>
              <h2 className="text-white text-3xl lg:text-[2.5rem] font-medium clash-font mb-4 leading-[110%]">
                {portfolioDetails?.name || "All Pro Color"}
              </h2>
              <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-8">
                {portfolioDetails?.description || "Description"}
              </p>
              <div className="flex flex-wrap gap-2">
                {portfolioDetails?.technology?.map(
                  (item: any, index: number) => (
                    <div
                      key={index}
                      className="px-4 py-2 lg:px-6 lg:py-3 bg-white/10 backdrop-blur-sm text-white text-xs lg:text-sm font-medium rounded-sm"
                    >
                      {item?.name}
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Right Content - Form */}
            <div className="w-full lg:w-[55%] bg-white px-6 lg:px-12 py-8 lg:py-10 lg:overflow-y-auto">
              <form onSubmit={handleSubmit} className="flex flex-col gap-5 lg:gap-6">
                <div className="mb-2 lg:mb-0">
                  <h1 className="text-[#0A0D14] text-2xl lg:text-[2.5rem] font-medium clash-font mb-3 leading-tight">
                    Looking To Discover Our Success Stories?
                  </h1>
                </div>

                {submissionStatus !== "idle" && (
                  <div
                    role="status"
                    className={clsx(
                      "p-3 rounded-md text-sm lg:text-base",
                      submissionStatus === "success"
                        ? "bg-green-50 border border-green-200 text-green-800"
                        : "bg-red-50 border border-red-200 text-red-800",
                    )}
                  >
                    {submissionMessage}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-[#0A0D14] text-sm font-medium">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      onFocus={ensureLoadRecaptcha}
                      value={formData?.name || ""}
                      onChange={handleChange("name")}
                      className="px-4 py-3 bg-white text-black placeholder:text-gray-400 outline-none border border-[#E5E7EB] focus:border-[#2D5BFF] rounded-md transition-colors text-sm lg:text-base"
                      aria-invalid={!!errors.fullName}
                    />
                    {errors.fullName && (
                      <div className="text-xs text-red-600">{errors.fullName}</div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-[#0A0D14] text-sm font-medium">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email id"
                      value={formData.email}
                      onChange={handleChange("email")}
                      className="px-4 py-3 bg-white text-black placeholder:text-gray-400 outline-none border border-[#E5E7EB] focus:border-[#2D5BFF] rounded-md transition-colors text-sm lg:text-base"
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && (
                      <div className="text-xs text-red-600">{errors.email}</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[#0A0D14] text-sm font-medium">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your company name"
                    value={formData.companyName}
                    onChange={handleChange("companyName")}
                    className="px-4 py-3 bg-white text-black placeholder:text-gray-400 outline-none border border-[#E5E7EB] focus:border-[#2D5BFF] rounded-md transition-colors text-sm lg:text-base"
                  />
                  {errors.companyName && (
                    <div className="text-xs text-red-600">{errors.companyName}</div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[#0A0D14] text-sm font-medium">
                    MS Team ID / WhatsApp Number
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your MS Team ID / WhatsApp number"
                    value={formData.msTeamWhatsapp}
                    onChange={handleChange("msTeamWhatsapp")}
                    className="px-4 py-3 bg-white text-black placeholder:text-gray-400 outline-none border border-[#E5E7EB] focus:border-[#2D5BFF] rounded-md transition-colors text-sm lg:text-base"
                  />
                  {errors.msTeamWhatsapp && (
                    <div className="text-xs text-red-600">
                      {errors.msTeamWhatsapp}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[#0A0D14] text-sm font-medium">
                    Specific Requirement
                  </label>
                  <textarea
                    ref={textareaRef}
                    placeholder="Enter your requirement"
                    value={formData.requirement}
                    onChange={handleChange("requirement")}
                    rows={3}
                    className="px-4 py-3 bg-white text-black placeholder:text-gray-400 outline-none border border-[#E5E7EB] focus:border-[#2D5BFF] rounded-md transition-colors resize-none text-sm lg:text-base"
                  />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="agree"
                    checked={formData.agreeToReceive}
                    onChange={handleChange("agreeToReceive")}
                    className="mt-1 w-4 h-4 border-2 border-gray-300 rounded accent-[#2D5BFF] shrink-0"
                  />
                  <label
                    htmlFor="agree"
                    className="text-xs lg:text-sm text-[#6B7280] leading-snug lg:leading-relaxed"
                  >
                    I agree to receive the requested file and occasional updates
                    from Konstant Infosolutions.
                  </label>
                </div>

                {recaptchaSiteKey ? (
                  <div className="flex items-start gap-3 overflow-hidden">
                    {loadRecaptcha && (
                      <div className="mt-2 scale-90 origin-left lg:scale-100">
                        <Script
                          id="google-recaptcha"
                          src="https://www.google.com/recaptcha/api.js?render=explicit"
                          strategy="lazyOnload"
                          async
                        />
                        <ReCAPTCHA
                          sitekey={recaptchaSiteKey}
                          onChange={(token: string | null) =>
                            setRecaptchaToken(token || "")
                          }
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-yellow-600">
                    reCAPTCHA not configured.
                  </div>
                )}
                {errors.captcha && (
                  <div className="text-xs text-red-600">{errors.captcha}</div>
                )}

                <div className="flex items-center justify-end mt-2 lg:mt-4">
                  <button
                    type="submit"
                    className="group inline-flex items-center select-none cursor-pointer g-recaptcha"
                    disabled={buttonDisable}
                  >
                    <span
                      className={clsx(
                        "h-12 lg:h-[var(--primarybtn-height)]",
                        "rounded-full flex items-center justify-center font-semibold whitespace-nowrap transition-colors px-6 lg:px-10 text-sm lg:text-[length:var(--primarybtn)] bg-white hover:bg-[#225BD4] hover:text-white text-[#00040b] border border-[#ECF0F6]",
                      )}
                    >
                      {submissionStatus === "submitting"
                        ? "Sending..."
                        : "Get the PDF"}
                    </span>

                    <span
                      className="w-12 h-12 lg:w-[var(--primarybtn-height)] lg:h-[var(--primarybtn-height)] ml-0 shrink-0 rounded-full grid place-items-center transition-colors bg-[#225BD4]"
                    >
                      <Image
                        src={Icons?.chevron}
                        alt=""
                        width={16}
                        height={16}
                        className="transition-transform duration-300 rotate-0 group-hover:rotate-[-90deg] invert"
                      />
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}